import { useRef } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { RocketLaunch as RocketLaunchIcon, Check as CheckIcon } from '../../icons';
import { landingColors, landingFont, eyebrowSx } from '../landingTheme';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import Hero3DMount from '../Hero3DMount';

const HERO_STATS = [
  { value: '60%', label: 'Less wait time' },
  { value: '3x', label: 'Faster orders' },
  { value: '0', label: 'Apps to install' },
];

export default function HeroSection({ onNavigate, onScrollToSection }) {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <Box
      ref={sectionRef}
      component="section"
      aria-label="Hero"
      sx={{
        position: 'relative',
        backgroundColor: landingColors.night,
        color: '#fff',
        overflow: 'hidden',
        pt: { xs: 12, md: 14 },
        pb: { xs: 8, md: 10 },
        minHeight: { md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-10%',
          right: '-10%',
          width: '70%',
          height: '90%',
          background: `radial-gradient(circle, ${landingColors.indigo}33 0%, transparent 60%)`,
          filter: 'blur(20px)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 }, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            gap: { xs: 2, md: 6 },
            alignItems: 'center',
          }}
        >
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              data-reveal
              sx={{
                ...eyebrowSx,
                color: landingColors.indigo,
                mb: 2,
                fontFamily: landingFont,
              }}
            >
              QR Ordering Platform
            </Typography>

            <Typography
              data-reveal
              data-reveal-delay="0.08"
              component="h1"
              sx={{
                fontFamily: landingFont,
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.02,
                fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.2rem', lg: '4.8rem' },
                mb: 2.5,
              }}
            >
              Scan. Order. Pay.
              <Box component="span" sx={{ display: 'block', color: landingColors.indigo }}>
                All from the table.
              </Box>
            </Typography>

            <Typography
              data-reveal
              data-reveal-delay="0.16"
              sx={{
                fontFamily: landingFont,
                color: landingColors.onNightMuted,
                fontSize: { xs: '1rem', md: '1.2rem' },
                lineHeight: 1.65,
                maxWidth: 520,
                mx: { xs: 'auto', md: 0 },
                mb: 4,
              }}
            >
              Customers scan a QR code, browse your menu, and pay instantly. Orders flow straight to your
              dashboard with real-time tracking and analytics to grow your restaurant.
            </Typography>

            <Box
              data-reveal
              data-reveal-delay="0.24"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                justifyContent: { xs: 'center', md: 'flex-start' },
                mb: 3,
              }}
            >
              <Button
                onClick={() => onNavigate('/register')}
                startIcon={<RocketLaunchIcon />}
                disableElevation
                sx={{
                  fontFamily: landingFont,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  px: 3.5,
                  py: 1.5,
                  borderRadius: '14px',
                  backgroundColor: landingColors.indigo,
                  color: '#fff',
                  transition: 'transform 0.2s ease, background-color 0.2s ease',
                  '&:hover': { backgroundColor: landingColors.indigoStrong, transform: 'translateY(-2px)' },
                }}
              >
                Start Free Trial
              </Button>
              <Button
                onClick={() => onScrollToSection('pricing-section')}
                variant="outlined"
                sx={{
                  fontFamily: landingFont,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  px: 3.5,
                  py: 1.5,
                  borderRadius: '14px',
                  borderColor: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                View Pricing
              </Button>
            </Box>

            <Typography
              data-reveal
              data-reveal-delay="0.3"
              sx={{
                fontFamily: landingFont,
                color: landingColors.onNightMuted,
                fontSize: '0.85rem',
                fontWeight: 500,
                mb: { xs: 0, md: 5 },
              }}
            >
              <CheckIcon sx={{ fontSize: '1rem', verticalAlign: '-0.18em', mr: 0.25, color: landingColors.indigo }} />
              No credit card &nbsp;•&nbsp; 14-day free trial &nbsp;•&nbsp; Cancel anytime
            </Typography>

            <Box
              data-reveal
              data-reveal-delay="0.36"
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 4,
                mt: 1,
              }}
            >
              {HERO_STATS.map((stat) => (
                <Box key={stat.label}>
                  <Typography sx={{ fontFamily: landingFont, fontWeight: 800, fontSize: '1.9rem', lineHeight: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontFamily: landingFont, color: landingColors.onNightMuted, fontSize: '0.85rem', mt: 0.5 }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              height: { xs: 360, sm: 440, md: 560 },
              width: '100%',
              // Feather the canvas edges so the 3D scene dissolves into the
              // dark hero instead of ending in a hard rectangle.
              WebkitMaskImage:
                'radial-gradient(ellipse 75% 75% at 50% 50%, #000 55%, transparent 100%)',
              maskImage: 'radial-gradient(ellipse 75% 75% at 50% 50%, #000 55%, transparent 100%)',
            }}
          >
            <Hero3DMount />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
